import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, Button, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material';
import { ChevronLeft as PrevIcon, ChevronRight as NextIcon, EventBusy as EmptyIcon, PlayArrow, BedtimeOutlined, CheckCircle } from '@mui/icons-material';
import {
  addDays, addMonths, addWeeks, endOfDay, endOfMonth, endOfWeek, format, getISODay,
  isSameDay, isSameMonth, startOfDay, startOfMonth, startOfWeek
} from 'date-fns';
import { primaryMuscleOf, muscleLabel } from 'components/play/constants';
import PlayRoutineDayModal from 'components/play/PlayRoutineDayModal';
import { timeFormat } from 'common/constants/date';
import { convertLocalToUtcTime, convertUtcToLocalTime } from 'common/utils/date';
import { buildSessionFiltersPerRole, isMembershipActive } from 'common/utils/session';
import { isClient } from 'common/utils/user';
import { getSessionLineColor, isPastSession, dimIfPastSx } from 'common/utils/sessionColor';
import { goldPulseSx, GOLD_PULSE_DURATION_MS } from 'common/theme/animations/goldPulse';
import { sessionListCalendarEventFields } from 'common/constants/query-fields/session';
import sessionSlice from 'store/modules/session';

const DEFAULT_EVENTS = [];

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Un día es "pasado" cuando terminó (no cuenta hoy): las rutinas de días idos se atenúan.
const isPastDay = (d) => endOfDay(d).getTime() < Date.now();
// Mobile: gutter de hora angosto + 7 columnas apiñadas (toda la semana sin scroll horizontal).
const GRID_COLS = { xs: '24px repeat(7, minmax(0, 1fr))', sm: '64px repeat(7, minmax(0, 1fr))' };
const trainerInitial = (trainer) => (trainer?.last_name?.[0] || trainer?.full_name?.[0] || '').toUpperCase();

/**
 * Agenda CONDENSADA del cliente: muestra SOLO las horas que tienen sesión en la semana visible
 * (salta las horas vacías del medio), por lo que con sesiones a las 8 y a las 17 se ven únicamente
 * esas franjas, no 8→17 continuo. Incluye vista de mes (resumen) y navegación por semana/mes.
 * Reusa los eventos del store (mismos que la agenda) y delega el clic al diálogo de detalle.
 *
 * OTRIX Play: cuando llega `routineByDay`, arriba de la rejilla horaria se pinta la SEMANA de la
 * rutina como una card por día (con "Iniciar rutina" / "Descanso"). Las rutinas son por día, no por
 * hora, por eso van en su propia tira y no dentro de las franjas.
 */
function CondensedWeekAgenda({
  events = DEFAULT_EVENTS,
  dateLocale,
  onEventClick = () => {},
  initialDate = null,
  routineByDay = null,
  onStartRoutine = () => {},
  playWorkouts = []
}) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(['common', 'sessions', 'play']);

  const role = useSelector((s) => s.auth.user.role);
  const userId = useSelector((s) => s.auth.user.id);
  const membershipStatus = useSelector((s) => s.auth.user?.membership_status);
  const sessionRefresh = useSelector((s) => s.session.refresh);
  // Membresía no vigente (mora/inactivo): se opacan TODAS las cards de sesión del cliente.
  const membershipInactive = isClient(role) && !isMembershipActive(membershipStatus);

  const [viewMode, setViewMode] = useState('week');
  const [cursor, setCursor] = useState(initialDate || new Date());
  // Día resaltado al venir de la vista de mes: su columna "parpadea" en dorado para guiar la mirada.
  const [highlightDayKey, setHighlightDayKey] = useState(null);
  // OTRIX Play — día de rutina abierto en el modal de detalle ({ day, items }).
  const [routineDay, setRoutineDay] = useState(null);

  const weekStart = useMemo(() => startOfWeek(cursor, { weekStartsOn: 0 }), [cursor]);
  const rangeStart = viewMode === 'week' ? weekStart : startOfMonth(cursor);
  const rangeEnd = viewMode === 'week' ? endOfWeek(cursor, { weekStartsOn: 0 }) : endOfMonth(cursor);
  const rangeStartMs = startOfDay(rangeStart).getTime();
  const rangeEndMs = endOfDay(rangeEnd).getTime();

  // Posicionar la semana al llegar por deep-link (?date=YYYY-MM-DD) UNA vez.
  useEffect(() => {
    if (initialDate) {
      setCursor(initialDate);
      setHighlightDayKey(format(initialDate, 'yyyy-MM-dd'));
    }
  }, [initialDate]);

  // El parpadeo dura ~3 ciclos; luego se limpia para poder re-dispararlo en el próximo salto.
  useEffect(() => {
    if (!highlightDayKey) return undefined;
    const id = setTimeout(() => setHighlightDayKey(null), GOLD_PULSE_DURATION_MS);
    return () => clearTimeout(id);
  }, [highlightDayKey]);

  const fetchRange = useCallback(
    (startMs, endMs) => {
      dispatch(
        sessionSlice.actions.listEvent({
          fields: sessionListCalendarEventFields,
          filters: buildSessionFiltersPerRole(role, userId, {
            start_between: {
              start_date: convertLocalToUtcTime(new Date(startMs)),
              end_date: convertLocalToUtcTime(new Date(endMs))
            }
          })
        })
      );
    },
    [dispatch, role, userId]
  );

  // Fetch al cambiar de rango (navegación / week-month).
  useEffect(() => {
    fetchRange(rangeStartMs, rangeEndMs);
  }, [rangeStartMs, rangeEndMs, fetchRange]);

  // Refetch inmediato cuando se crea/borra una sesión.
  useEffect(() => {
    if (sessionRefresh) fetchRange(rangeStartMs, rangeEndMs);
  }, [sessionRefresh]);

  // Normaliza cada evento a su día/hora LOCAL (vía convertUtcToLocalTime, fuente de verdad de la app).
  const norm = useMemo(
    () =>
      (events || []).map((e) => ({
        raw: e,
        dayKey: convertUtcToLocalTime(e.start, 'yyyy-MM-dd'),
        hour: Number(convertUtcToLocalTime(e.start, 'H')),
        timeLabel: `${convertUtcToLocalTime(e.start, timeFormat)} – ${convertUtcToLocalTime(e.end, timeFormat)}`
      })),
    [events]
  );

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const weekEvents = useMemo(() => {
    const dayKeys = new Set(weekDays.map((d) => format(d, 'yyyy-MM-dd')));
    return norm.filter((n) => dayKeys.has(n.dayKey));
  }, [norm, weekDays]);

  const activeHours = useMemo(() => {
    const set = new Set(weekEvents.map((n) => n.hour));
    return [...set].sort((a, b) => a - b);
  }, [weekEvents]);

  const go = (dir) => setCursor((c) => (viewMode === 'week' ? addWeeks(c, dir) : addMonths(c, dir)));
  const goToday = () => setCursor(new Date());

  const title =
    viewMode === 'week'
      ? `${format(weekStart, 'd MMM', { locale: dateLocale })} – ${format(endOfWeek(cursor, { weekStartsOn: 0 }), 'd MMM yyyy', { locale: dateLocale })}`
      : capitalize(format(cursor, 'MMMM yyyy', { locale: dateLocale }));

  // Etiqueta compacta "1pm", "4am", "12pm" (aprovecha el ancho; consistente con el calendario del entrenador).
  const hourLabel = (h) => `${(h % 12) || 12}${h < 12 ? 'am' : 'pm'}`;

  // Parpadeo dorado (encendido/apagado) de la columna del día al que saltamos desde el mes.
  // Efecto centralizado en common/theme/animations/goldPulse (se reusa en entrenador/admin/disponibilidad).
  const pulseFor = useCallback((dayKey) => goldPulseSx(theme, highlightDayKey === dayKey), [theme, highlightDayKey]);

  const renderEventCard = (n) => {
    const e = n.raw;
    const ext = e.extendedProps || {};
    const { trainer } = ext;
    // Las sesiones grupales traen `sessionTypeName`; las 1-a-1, `session_type`. Para Active Plus
    // anteponemos el distintivo de membresía: "Active Plus · Full Body" (o solo "Active Plus").
    const typeName = ext.sessionTypeName || ext.session_type;
    let categoryLabel = typeName;
    if (ext.isGroupSession) {
      // Si el tipo ya es "active plus" (o no hay), no se duplica: solo "Active Plus".
      const showType = typeName && !typeName.trim().toLowerCase().startsWith('active plus');
      categoryLabel = showType ? `Active Plus · ${typeName}` : 'Active Plus';
    }
    const lineColor = getSessionLineColor(theme, ext.status, e.start);
    const past = isPastSession(e.start, e.end);
    const initials = trainerInitial(trainer);
    const firstName = (trainer?.full_name || '').split(' ')[0];
    // Rango compacto para mobile: "1–2p".
    const shortTime = `${convertUtcToLocalTime(e.start, 'h')}–${convertUtcToLocalTime(e.end, 'haaaaa')}`;

    const baseSx = {
      cursor: 'pointer',
      backgroundColor: 'surface.raised',
      border: '1px solid',
      borderColor: 'hairline.main',
      borderLeft: `4px solid ${lineColor}`,
      borderRadius: '10px',
      transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), background-color 0.2s',
      '&:hover': { transform: 'translateY(-2px)', backgroundColor: 'surface.hover' },
      '&:active': { transform: 'scale(0.98)' },
      ...dimIfPastSx(past || membershipInactive)
    };

    // MOBILE: card más alta que ancha. Inicial del apellido + categoría + horas. El stripe SIEMPRE visible.
    if (isMobile) {
      return (
        <Box
          key={e.id}
          role="button"
          tabIndex={0}
          onClick={() => onEventClick(e)}
          onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onEventClick(e); } }}
          sx={{ ...baseSx, p: 0.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: 0.3 }}>
          <Avatar src={trainer?.photo} alt={trainer?.full_name} sx={{ width: 36, height: 36, fontSize: '0.9rem', bgcolor: 'gold.soft', color: 'gold.main' }}>
            {initials}
          </Avatar>
          {categoryLabel && (
            <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: 'secondary.main', lineHeight: 1.1, width: '100%' }} noWrap>
              {categoryLabel}
            </Typography>
          )}
          {firstName && (
            <Typography sx={{ fontSize: '0.56rem', color: 'text.primary', lineHeight: 1.1, width: '100%' }} noWrap>
              {firstName}
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary', fontWeight: 600 }} noWrap>
            {shortTime}
          </Typography>
        </Box>
      );
    }

    // DESKTOP/TABLET: foto del entrenador protagonista + categoría + nombre + horas.
    return (
      <Box
        key={e.id}
        role="button"
        tabIndex={0}
        onClick={() => onEventClick(e)}
        onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onEventClick(e); } }}
        sx={{ ...baseSx, p: 1, display: 'flex', gap: 1.25, alignItems: 'center' }}>
        <Avatar src={trainer?.photo} alt={trainer?.full_name} sx={{ width: 46, height: 46, fontSize: '1rem', flexShrink: 0, bgcolor: 'gold.soft', color: 'gold.main' }}>
          {initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          {categoryLabel && (
            <Typography variant="caption" sx={{ color: 'secondary.main', fontWeight: 700, display: 'block' }} noWrap>
              {categoryLabel}
            </Typography>
          )}
          {trainer && (
            <Typography variant="caption" sx={{ color: 'text.primary', display: 'block' }} noWrap>
              {trainer.full_name}
            </Typography>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontWeight: 600 }} noWrap>
            {n.timeLabel}
          </Typography>
        </Box>
      </Box>
    );
  };

  // OTRIX Play — card de rutina de un día (o "Descanso" si ese día no tiene ejercicios).
  // Tocar la card abre el detalle del día; el botón interno arranca el entrenamiento directo.
  const renderRoutineCell = (d) => {
    const iso = getISODay(d); // 1=Lun .. 7=Dom
    const items = routineByDay?.[iso] || [];
    const today = isSameDay(d, new Date());
    const isRest = items.length === 0;
    // Los días ya pasados se atenúan (mismo lenguaje que las sesiones pasadas).
    const past = isPastDay(d);
    // Entrenamiento registrado ESE día (fecha real, no día de la semana): pinta el logro.
    const dayWorkout = (playWorkouts || []).find((w) => {
      const when = w.completed_at || w.started_at;
      return when && isSameDay(new Date(when), d);
    });

    if (isRest) {
      // A 320 la columna deja ~30px y la palabra "Descanso" se recortaba: la luna se entiende
      // sola. En pantallas con sitio se acompaña del texto.
      return (
        <Stack alignItems="center" justifyContent="center" spacing={0.25} sx={{ height: '100%', py: 1, ...dimIfPastSx(past) }}>
          <BedtimeOutlined sx={{ fontSize: { xs: 14, sm: 16 }, color: 'text.disabled' }} titleAccess={t('home.week.rest', { ns: 'play' })} />
          {!isMobile && (
            <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', fontWeight: 600 }} noWrap>
              {t('home.week.rest', { ns: 'play' })}
            </Typography>
          )}
        </Stack>
      );
    }

    return (
      <Stack
        spacing={0.5}
        role="button"
        tabIndex={0}
        onClick={() => setRoutineDay({ day: iso, items, past, today, workout: dayWorkout || null })}
        onKeyDown={(ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            setRoutineDay({ day: iso, items, past, today, workout: dayWorkout || null });
          }
        }}
        sx={{
          height: '100%',
          p: { xs: 0.5, sm: 1 },
          cursor: 'pointer',
          borderRadius: '10px',
          border: '1px solid',
          borderColor: today ? 'gold.main' : 'hairline.main',
          backgroundColor: 'surface.raised',
          transition: 'transform 0.2s cubic-bezier(0.32,0.72,0,1), border-color 0.2s',
          '&:hover': { transform: 'translateY(-2px)', borderColor: 'gold.main' },
          '&:active': { transform: 'scale(0.98)' },
          ...dimIfPastSx(past)
        }}>
        <Typography
          sx={{ fontSize: { xs: '0.58rem', sm: '0.75rem' }, fontWeight: 800, color: today ? 'gold.main' : 'text.primary' }}
          noWrap>
          {muscleLabel(t, primaryMuscleOf(items))}
        </Typography>
        {!isMobile && (
          <Typography variant="caption" sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }} noWrap>
            {t('agenda.exercises', { ns: 'play', count: items.length })}
          </Typography>
        )}
        {/* Día con entrenamiento registrado: check verde (o ámbar si quedó a medias) + kcal. */}
        {dayWorkout && (
          <Stack direction="row" spacing={0.4} alignItems="center" sx={{ minWidth: 0 }}>
            <CheckCircle
              sx={{
                fontSize: 13,
                color: dayWorkout.status === 'completed' ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg'
              }}
            />
            {!isMobile && dayWorkout.estimated_calories > 0 && (
              <Typography
                sx={{ fontSize: '0.62rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: dayWorkout.status === 'completed' ? 'sessionStatus.completed.fg' : 'sessionStatus.pending.fg' }}
                noWrap>
                {t('todayCard.kcal', { ns: 'play', count: dayWorkout.estimated_calories })}
              </Typography>
            )}
          </Stack>
        )}
        <Box sx={{ flex: 1 }} />
        {/* SOLO el día de HOY se puede iniciar: los pasados y los futuros se consultan. */}
        <Button
          size="small"
          disabled={!today}
          startIcon={!isMobile ? <PlayArrow sx={{ fontSize: 14 }} /> : undefined}
          onClick={(ev) => {
            // La card abre el detalle; el botón arranca directo (sin abrir el modal).
            ev.stopPropagation();
            onStartRoutine(iso, items);
          }}
          sx={{
            minWidth: 0,
            minHeight: 0,
            px: { xs: 0.5, sm: 1 },
            py: 0.25,
            fontSize: { xs: '0.55rem', sm: '0.68rem' },
            fontWeight: 800,
            textTransform: 'none',
            color: 'gold.main',
            '&:active': { transform: 'scale(0.98)' }
          }}>
          {isMobile ? <PlayArrow sx={{ fontSize: 14 }} /> : t('agenda.start', { ns: 'play' })}
        </Button>
      </Stack>
    );
  };

  const renderRoutineStrip = () => (
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="caption"
        sx={{ display: 'block', px: 0.5, py: 0.75, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
        {t('agenda.weekRoutine', { ns: 'play' })}
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, borderBottom: '1px solid', borderColor: 'hairline.main', pb: 1, minHeight: { xs: 84, sm: 96 } }}>
        <Box />
        {weekDays.map((d) => (
          <Box key={`routine-${d.toISOString()}`} sx={{ p: { xs: 0.25, sm: 0.5 }, minWidth: 0, ...pulseFor(format(d, 'yyyy-MM-dd')) }}>
            {renderRoutineCell(d)}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderWeek = () => (
    <Box>
      {/* Cabecera de días — toda la semana cabe sin scroll (apiñada en mobile). */}
      <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'surface.content' }}>
        <Box />
        {weekDays.map((d) => {
          const today = isSameDay(d, new Date());
          return (
            <Box key={d.toISOString()} sx={{ textAlign: 'center', py: { xs: 0.75, sm: 1.25 }, borderBottom: '1px solid', borderColor: 'hairline.main', ...pulseFor(format(d, 'yyyy-MM-dd')) }}>
              <Typography sx={{ fontSize: { xs: '0.62rem', sm: '0.75rem' }, textTransform: 'uppercase', fontWeight: 800, letterSpacing: '0.02em', color: today ? 'secondary.main' : 'text.secondary' }}>
                {format(d, isMobile ? 'EEEEE' : 'EEE', { locale: dateLocale })}
              </Typography>
              <Typography sx={{ fontSize: { xs: '0.58rem', sm: '0.72rem' }, display: 'block', color: today ? 'secondary.main' : 'text.disabled', fontWeight: 700 }}>
                {format(d, isMobile ? 'd' : 'd/M', { locale: dateLocale })}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* OTRIX Play — la semana de la rutina, arriba de las franjas horarias. */}
      {routineByDay && renderRoutineStrip()}

      {activeHours.length === 0 ? (
        // En Play la tira de rutina ya es el contenido: no se anuncia "no tienes sesiones"
        // (esa vacuidad se refiere a las sesiones 1:1, que en Play normalmente no existen).
        !routineByDay && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <EmptyIcon sx={{ fontSize: 38, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('noSessionsThisWeek', { ns: 'sessions', defaultValue: 'No tienes sesiones esta semana.' })}
            </Typography>
          </Box>
        )
      ) : (
        activeHours.map((h) => (
          <Box key={h} sx={{ display: 'grid', gridTemplateColumns: GRID_COLS, borderBottom: '1px solid', borderColor: 'hairline.main', minHeight: { xs: 112, sm: 96 } }}>
            <Box sx={{ pr: { xs: 0.25, sm: 1 }, pt: 1, textAlign: 'right' }}>
              <Typography sx={{ fontSize: { xs: '0.55rem', sm: '0.75rem' }, color: 'text.secondary', fontWeight: 700, whiteSpace: 'nowrap' }}>
                {isMobile ? `${(h % 12) || 12}${h < 12 ? 'am' : 'pm'}` : hourLabel(h)}
              </Typography>
            </Box>
            {weekDays.map((d) => {
              const dayKey = format(d, 'yyyy-MM-dd');
              const cell = weekEvents.filter((n) => n.dayKey === dayKey && n.hour === h);
              return (
                <Box key={dayKey} sx={{ p: { xs: 0.25, sm: 0.5 }, borderLeft: '1px solid', borderColor: 'hairline.main', display: 'flex', flexDirection: 'column', gap: 0.5, ...pulseFor(dayKey) }}>
                  {cell.map(renderEventCard)}
                </Box>
              );
            })}
          </Box>
        ))
      )}
    </Box>
  );

  const monthDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 0 });
    const days = [];
    let d = start;
    while (d <= end) {
      days.push(d);
      d = addDays(d, 1);
    }
    return days;
  }, [cursor]);

  const eventsByDay = useMemo(() => {
    const map = {};
    norm.forEach((n) => {
      map[n.dayKey] = map[n.dayKey] || [];
      map[n.dayKey].push(n);
    });
    return map;
  }, [norm]);

  const renderMonth = () => (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', mb: 0.5 }}>
        {monthDays.slice(0, 7).map((d) => (
          <Typography key={d.toISOString()} variant="caption" sx={{ textAlign: 'center', textTransform: 'uppercase', fontWeight: 800, color: 'text.secondary', py: 0.5 }}>
            {format(d, 'EEE', { locale: dateLocale })}
          </Typography>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 0.5 }}>
        {monthDays.map((d) => {
          const dayKey = format(d, 'yyyy-MM-dd');
          const dayEvents = eventsByDay[dayKey] || [];
          const inMonth = isSameMonth(d, cursor);
          const today = isSameDay(d, new Date());
          const past = isPastDay(d);
          // OTRIX Play — la rutina se repite cada semana: un día del mes es de entreno si su
          // día de la semana tiene ejercicios.
          const routineItems = routineByDay?.[getISODay(d)] || [];
          const hasRoutine = routineItems.length > 0;
          const clickable = dayEvents.length > 0 || hasRoutine;

          return (
            <Box
              key={dayKey}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => { setCursor(d); setViewMode('week'); setHighlightDayKey(dayKey); } : undefined}
              sx={{
                minHeight: 84,
                p: 0.75,
                borderRadius: '10px',
                border: '1px solid',
                // Un día con entreno se marca en dorado (igual que las sesiones marcan el suyo).
                borderColor: (() => {
                  if (today) return 'secondary.main';
                  return hasRoutine ? 'gold.main' : 'hairline.main';
                })(),
                backgroundColor: hasRoutine && !past ? 'gold.soft' : 'surface.raised',
                opacity: inMonth ? 1 : 0.4,
                cursor: clickable ? 'pointer' : 'default',
                transition: 'background-color 0.2s',
                '&:hover': clickable ? { backgroundColor: 'surface.hover' } : {},
                ...dimIfPastSx(past)
              }}>
              <Typography variant="caption" sx={{ fontWeight: 800, color: today ? 'secondary.main' : 'text.primary' }}>
                {format(d, 'd')}
              </Typography>
              <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                {hasRoutine && (
                  <Typography variant="caption" sx={{ color: 'gold.main', fontSize: '0.65rem', fontWeight: 800, lineHeight: 1.2 }} noWrap>
                    • {muscleLabel(t, primaryMuscleOf(routineItems))}
                  </Typography>
                )}
                {dayEvents.slice(0, 2).map((n) => (
                  <Typography key={n.raw.id} variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1.2 }} noWrap>
                    • {n.raw.extendedProps?.session_type || convertUtcToLocalTime(n.raw.start, timeFormat)}
                  </Typography>
                ))}
                {dayEvents.length > 2 && (
                  <Typography variant="caption" sx={{ color: 'gold.main', fontSize: '0.65rem', fontWeight: 700 }}>
                    +{dayEvents.length - 2}
                  </Typography>
                )}
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box>
      {/* Toolbar */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
          <IconButton size="small" onClick={() => go(-1)} aria-label="anterior" sx={{ color: 'text.secondary' }}><PrevIcon /></IconButton>
          <IconButton size="small" onClick={() => go(1)} aria-label="siguiente" sx={{ color: 'text.secondary' }}><NextIcon /></IconButton>
          <Button size="small" variant="text" color="inherit" onClick={goToday} sx={{ color: 'text.primary', textTransform: 'none', fontWeight: 700 }}>
            {t('today', { ns: 'common', defaultValue: 'Hoy' })}
          </Button>
        </Stack>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={viewMode}
          onChange={(_, v) => { if (v) setViewMode(v); }}
          sx={{
            '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 2, color: 'text.secondary', borderColor: 'hairline.strong' },
            '& .Mui-selected': { color: (th) => `${th.palette.secondary.main} !important`, backgroundColor: (th) => `${th.palette.gold.soft} !important` }
          }}>
          <ToggleButton value="week">{t('week', { ns: 'common', defaultValue: 'Semana' })}</ToggleButton>
          <ToggleButton value="month">{t('month', { ns: 'common', defaultValue: 'Mes' })}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: '14px', p: { xs: 1, sm: 1.5 }, overflow: 'hidden' }}>
        {viewMode === 'week' ? renderWeek() : renderMonth()}
      </Box>

      {/* OTRIX Play — detalle del día (qué toca y cómo se hace) al tocar su card. */}
      <PlayRoutineDayModal
        open={Boolean(routineDay)}
        dayOfWeek={routineDay?.day}
        items={routineDay?.items || []}
        // SOLO el día de HOY se arranca; pasados y futuros se consultan.
        canStart={Boolean(routineDay?.today)}
        isPast={Boolean(routineDay?.past)}
        workout={routineDay?.workout || null}
        onClose={() => setRoutineDay(null)}
        onStart={() => {
          onStartRoutine(routineDay.day, routineDay.items);
          setRoutineDay(null);
        }}
      />
    </Box>
  );
}

CondensedWeekAgenda.propTypes = {
  events: PropTypes.arrayOf(PropTypes.object),
  dateLocale: PropTypes.object.isRequired,
  onEventClick: PropTypes.func,
  initialDate: PropTypes.instanceOf(Date),
  /** OTRIX Play — items de la rutina agrupados por día ISO (1=Lun..7=Dom). Null = no es Play. */
  routineByDay: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.object)),
  /** (dayOfWeek, dayItems) => void — arranca el motor de ejecución de ese día. */
  onStartRoutine: PropTypes.func,
  /** OTRIX Play — entrenamientos registrados (con fecha real): pintan el día completado. */
  playWorkouts: PropTypes.arrayOf(PropTypes.object)
};

export default CondensedWeekAgenda;
